import React from "react";
import { NavLink } from "react-router-dom";
import {
    Activity, LayoutGrid, Database, ShieldCheck, Network,
    AlertTriangle, Lightbulb, BarChart3, Gauge, ClipboardCheck, FileText, Stethoscope, Radar,
    Settings, UserCog, Info, Shield, Workflow, GitCompareArrows, Share2, Swords, BookText,
    Globe2, BookMarked, Zap, Cog, Wand2, FileCode2, CalendarClock,
} from "lucide-react";
import { ThemeSwitcher } from "@/components/common/ThemeSwitcher";
import { TID } from "@/lib/testIds";
import { useCapabilities } from "@/lib/capabilities";

export const SECTIONS = (caps) => [
    {
        label: "Operations",
        items: [
            { slug: "utsav", to: "/utsav", icon: Wand2, label: "Utsav — AI Agent", show: caps?.can_utsav_agent ?? true },
            { slug: "overview", to: "/", icon: Activity, label: "Executive Overview", show: true },
            { slug: "workspaces", to: "/workspaces", icon: LayoutGrid, label: "Workspaces", show: true },
            { slug: "compare", to: "/compare", icon: GitCompareArrows, label: "Compare", show: true },
        ],
    },
    {
        label: "Engineering Engines",
        items: [
            { slug: "authorization", to: "/authorization", icon: ShieldCheck, label: "Authorization", show: caps?.can_authorization ?? true },
            { slug: "code-review", to: "/code-review", icon: FileCode2, label: "Code Review / RICEFW", show: caps?.can_code_review ?? true },
            { slug: "su24", to: "/su24", icon: Database, label: "SU24 Engineering", show: caps?.can_su24 ?? true },
            { slug: "odata", to: "/odata", icon: Workflow, label: "OData Engineering", show: caps?.can_odata ?? true },
        ],
    },
    {
        label: "Role Engineering",
        items: [
            { slug: "roles", to: "/roles", icon: Swords, label: "Sentinel Forge", show: caps?.can_role_engineering ?? true },
            { slug: "instant-analysis", to: "/instant-analysis", icon: Zap, label: "Instant Analysis", show: caps?.can_instant_analysis ?? true },
        ],
    },
    {
        label: "Governance",
        items: [
            { slug: "governance-map", to: "/governance/map", icon: Globe2, label: "Compliance Map", show: caps?.can_governance ?? true },
            { slug: "mitigation-library", to: "/governance/mitigation-library", icon: BookMarked, label: "Mitigation Library", show: caps?.can_governance ?? true },
            { slug: "ruleset-config", to: "/admin/ruleset", icon: Cog, label: "Ruleset Configuration", show: !!caps?.is_admin },
            { slug: "posture-digest", to: "/admin/digest", icon: CalendarClock, label: "Posture Digest", show: !!caps?.is_admin },
        ],
    },
    {
        label: "Intelligence",
        items: [
            { slug: "graph", to: "/graph", icon: Network, label: "Knowledge Graph", show: true },
            { slug: "findings", to: "/findings", icon: AlertTriangle, label: "Findings", show: true },
            { slug: "recommendations", to: "/recommendations", icon: Lightbulb, label: "Recommendations", show: true },
        ],
    },
    {
        label: "Posture",
        items: [
            { slug: "risk", to: "/risk", icon: BarChart3, label: "Risk Dashboard", show: true },
            { slug: "compliance", to: "/compliance", icon: ClipboardCheck, label: "Compliance", show: true },
            { slug: "clean-core", to: "/clean-core", icon: Gauge, label: "Clean Core", show: true },
            { slug: "diagnostics", to: "/diagnostics", icon: Stethoscope, label: "Security Diagnostic", show: caps?.modules?.includes("diagnostics") ?? true },
            { slug: "ueba", to: "/ueba", icon: Radar, label: "Behaviour Analytics", show: caps?.modules?.includes("ueba") ?? true },
            { slug: "reports", to: "/reports", icon: FileText, label: "Executive Reports", show: caps?.can_reports ?? true },
        ],
    },
    {
        label: "System",
        items: [
            { slug: "admin", to: "/admin", icon: UserCog, label: "Administration", show: !!caps?.is_admin },
            { slug: "share", to: "/share", icon: Share2, label: "Share Hub", show: caps?.can_share_hub ?? true },
            { slug: "docs", to: "/docs", icon: BookText, label: "Documentation", show: true },
            { slug: "settings", to: "/settings", icon: Settings, label: "Settings", show: true },
            { slug: "about", to: "/about", icon: Info, label: "About", show: true },
        ],
    },
];

/** The navigation column itself. Shared by the desktop aside and the mobile
 * drawer so both always expose exactly the same module map. */
export function SidebarContent({ onNavigate }) {
    const { caps } = useCapabilities();
    const sections = SECTIONS(caps);
    return (
        <>
            <div className="px-5 py-6 border-b border-white/5">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <div className="chrome-bevel w-9 h-9 rounded-sm bg-gradient-to-br from-cyan-300 to-cyan-600 flex items-center justify-center shadow-[0_0_16px_rgb(var(--accent-1-rgb) / 0.4)]">
                            <Shield size={18} className="text-slate-950" strokeWidth={2.5} />
                        </div>
                        <span className="absolute -bottom-1 -right-1 radar-dot" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-display text-[15px] tracking-wide text-white">UTSAVA <span className="text-cyan-400">Genesis</span></div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-500/70">um-alphabydesign</div>
                    </div>
                </div>
            </div>

            <nav className="flex-1 overflow-y-auto py-4">
                {sections.map((sec) => {
                    const items = sec.items.filter((it) => it.show);
                    if (items.length === 0) return null;
                    return (
                        <div key={sec.label} className="mb-6">
                            <div className="px-5 mb-2 label-eyebrow">{sec.label}</div>
                            <ul>
                                {items.map((it) => (
                                    <li key={it.slug}>
                                        <NavLink
                                            to={it.to}
                                            end={it.to === "/"}
                                            data-testid={it.slug === "ruleset-config" ? TID.rulesetConfigBtn : TID.navItem(it.slug)}
                                            className={({ isActive }) =>
                                                `group flex items-center gap-3 mx-3 px-3 py-2 text-[13px] rounded-sm transition-all ${
                                                    isActive
                                                        ? "bg-cyan-400/10 text-cyan-300 border-l-2 border-cyan-400 shadow-[inset_0_0_30px_rgb(var(--accent-1-rgb) / 0.06)]"
                                                        : "text-slate-400 hover:text-white hover:bg-white/[0.03] border-l-2 border-transparent"
                                                }`
                                            }
                                            onClick={onNavigate}
                                        >
                                            <it.icon size={15} strokeWidth={1.5} />
                                            <span className="truncate">{it.label}</span>
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </nav>

            <div className="border-t border-white/5">
                <ThemeSwitcher />
            </div>

            <div className="px-5 py-4 border-t border-white/5 text-[10px] font-mono uppercase tracking-[0.15em] text-slate-400">
                <div className="flex items-center justify-between">
                    <span>Offline Engineering</span>
                    <span className="flex items-center gap-1.5 text-cyan-400/80">
                        <span className="dot" style={{ background: "var(--neon-cyan)", boxShadow: "0 0 8px var(--neon-cyan)" }} />
                        Secure
                    </span>
                </div>
            </div>
        </>
    );
}

export default function Sidebar() {
    return (
        <aside
            data-testid={TID.sidebar}
            className="hidden md:flex flex-col w-[260px] shrink-0 border-r border-white/8 border-t border-t-white/10 bg-[#0A0A14]/75 backdrop-blur-2xl relative z-10 shadow-[inset_-1px_0_0_rgba(0,0,0,0.6)]"
        >
            <SidebarContent />
        </aside>
    );
}
